import { useMemo, useState } from "react";

// ---------------------------------------------------------------------------
// CuteErrorBubble — turns the raw provider/server error string into a short,
// friendly one-liner with an emoji and (optionally) a "retry in Xs" hint.
//
// Pattern-matches on the most common Gemini errors first; falls back to a
// generic message with the original error tucked behind a "details" toggle.
// ---------------------------------------------------------------------------

interface ParsedError {
  emoji: string;
  title: string;
  message: string;
  retryAfterSec?: number;
  /** Full raw text, for the optional details expander. */
  raw: string;
  /** Colour tone (warm = soft warn, cold = critical). */
  tone: "warm" | "cold";
}

function parseError(raw: string): ParsedError {
  const text = raw ?? "";

  // ---- Rate limit / quota ------------------------------------------------
  // Tight matching only — must look like an actual rate-limit signal, not
  // a docs URL containing the words. Specifically:
  //   - explicit "<Provider> 429" header from our adapters
  //   - "429:" status code in the message body
  //   - Gemini's RESOURCE_EXHAUSTED enum
  //   - Anthropic's rate_limit_error type
  if (
    /\b(?:Gemini|Anthropic|OpenAI|Provider)\s+429\b/i.test(text) ||
    /\b429\s*:/.test(text) ||
    /RESOURCE_EXHAUSTED/.test(text) ||
    /rate_limit_error/.test(text) ||
    /\brate_limit_exceeded\b/.test(text) ||
    /\bquota[_ ]?exceeded\b/i.test(text)
  ) {
    const retryMatch = text.match(/retry[- ]?(?:in|after)?\s*[:=]?\s*([0-9.]+)\s*s/i);
    const retry = retryMatch ? Math.ceil(Number(retryMatch[1])) : undefined;
    // Anthropic-specific: per-organization tokens-per-minute cap.
    // Distinguish this from a quota / credit issue — users get confused
    // when they see "rate limit" and assume their balance is the problem.
    const tpmMatch = text.match(/([0-9,]+)\s*input tokens? per minute/i);
    const isTpm = !!tpmMatch || /tokens?\s*per\s*minute/i.test(text);
    if (isTpm) {
      const limit = tpmMatch ? tpmMatch[1] : null;
      return {
        emoji: "⚡",
        title: "Tokens-per-minute cap hit",
        message:
          (limit
            ? `Your Anthropic org is capped at ${limit} input tokens/min on this model. `
            : "Your Anthropic org has a per-minute token cap on this model. ") +
          "This is NOT a credit-balance issue. " +
          "Wait ~30s, switch to a model with higher limits (Sonnet 4.6), or have your admin upgrade the Anthropic usage tier.",
        retryAfterSec: 30,
        tone: "warm",
        raw,
      };
    }
    return {
      emoji: "🌬️",
      title: "Taking a quick breather",
      message: retry
        ? `Hit a rate limit. Try again in about ${retry}s.`
        : "Hit a rate limit. Try again in a few seconds.",
      retryAfterSec: retry,
      tone: "warm",
      raw,
    };
  }

  // ---- Credits / billing (Anthropic-specific) ----------------------------
  if (/credit.*balance|low.*credit|insufficient.*funds|billing/i.test(text)) {
    return {
      emoji: "💳",
      title: "Credit balance low",
      message: "The AI provider account is out of credit. Ask your admin to top up.",
      tone: "cold",
      raw,
    };
  }

  // ---- Auth / key issues -------------------------------------------------
  if (
    /\b401\b|UNAUTHENTICATED|invalid.*api.*key|API_KEY_INVALID|authentication_error/i.test(text)
  ) {
    return {
      emoji: "🔑",
      title: "Key trouble",
      message: "The AI provider rejected the API key. Ask your admin to check the API key setup.",
      tone: "cold",
      raw,
    };
  }

  // ---- Bad input / tool schema (4xx, before generic 5xx) ----------------
  if (
    /invalid_request_error|input_schema|tools\[\d+\]|messages\[\d+\]|max_tokens/i.test(text)
  ) {
    return {
      emoji: "🧩",
      title: "Bad input shape",
      message:
        "The AI provider rejected the request shape. Open details — usually a tool schema or message-format issue.",
      tone: "cold",
      raw,
    };
  }

  // ---- Permission / 403 --------------------------------------------------
  if (/403|PERMISSION_DENIED/i.test(text)) {
    return {
      emoji: "🚧",
      title: "Permission denied",
      message: "Not allowed to call the AI service right now. Reach out to your workspace admin.",
      tone: "cold",
      raw,
    };
  }

  // ---- Server overload / 5xx --------------------------------------------
  if (/5\d\d|UNAVAILABLE|overload/i.test(text)) {
    return {
      emoji: "☁️",
      title: "Gemini's having a moment",
      message: "The model is busy. Give it ~10 seconds and try again.",
      tone: "warm",
      raw,
    };
  }

  // ---- AI disabled for tenant -------------------------------------------
  if (/AI_DISABLED|AI features are not enabled/i.test(text)) {
    return {
      emoji: "🔌",
      title: "AI is turned off",
      message: "Ask a workspace admin to enable AI Assistant under Settings → AI Assistant.",
      tone: "cold",
      raw,
    };
  }

  // ---- Network / aborted -------------------------------------------------
  if (/network|fetch|abort|ECONN/i.test(text)) {
    return {
      emoji: "📡",
      title: "Connection wobbled",
      message: "Couldn't reach the server. Check your connection and retry.",
      tone: "warm",
      raw,
    };
  }

  // ---- Fallback ----------------------------------------------------------
  return {
    emoji: "🤔",
    title: "Something went sideways",
    message: "Couldn't finish that one. Please try again.",
    tone: "warm",
    raw,
  };
}

export default function CuteErrorBubble({ message }: { message: string }) {
  const parsed = useMemo(() => parseError(message), [message]);
  const [showDetails, setShowDetails] = useState(false);

  const palette =
    parsed.tone === "cold"
      ? { bg: "#fef2f2", border: "#fecaca", title: "#991b1b", body: "#7f1d1d" }
      : { bg: "#fff7ed", border: "#fed7aa", title: "#9a3412", body: "#7c2d12" };

  return (
    <div
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 12,
        padding: "10px 12px",
        fontSize: 12.5,
        lineHeight: 1.5,
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
      }}
    >
      <span style={{ fontSize: 18, lineHeight: 1.1, flexShrink: 0 }}>{parsed.emoji}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: palette.title, marginBottom: 2 }}>
          {parsed.title}
        </div>
        <div style={{ color: palette.body }}>{parsed.message}</div>
        {parsed.raw && parsed.raw !== parsed.message && (
          <button
            onClick={() => setShowDetails((v) => !v)}
            style={{
              marginTop: 6,
              padding: 0,
              background: "transparent",
              border: 0,
              color: palette.body,
              fontSize: 11,
              cursor: "pointer",
              opacity: 0.7,
              textDecoration: "underline",
            }}
          >
            {showDetails ? "hide details" : "details"}
          </button>
        )}
        {showDetails && (
          <pre
            style={{
              marginTop: 6,
              padding: 8,
              background: "rgba(0,0,0,0.04)",
              borderRadius: 6,
              fontSize: 10.5,
              maxHeight: 140,
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              color: palette.body,
            }}
          >
            {parsed.raw}
          </pre>
        )}
      </div>
    </div>
  );
}
